import { supabase } from '@/lib/supabase';
import { ProjectWithContractors } from '@/shared/types/api';

export interface LastTrackerOrg {
  id: number;
  name: string | null;
  image_url: string | null;
}

export const getKpisWithLatestEntries = async (
  projects: ProjectWithContractors[],
) => {
  if (!projects || projects.length === 0) {
    return [];
  }

  const projectIds = projects.map((p) => p.id);

  const { data: kpisData, error: kpisError } = await supabase
    .from('project_kpis')
    .select(
      `
      *,
      project:projects!project_kpis_project_id_fkey(
        id,
        name,
        category,
        type,
        start_date,
        end_date
      )
    `,
    )
    .in('project_id', projectIds);

  if (kpisError) {
    throw kpisError;
  }

  if (!kpisData || kpisData.length === 0) {
    return [];
  }

  const { data: metricsData, error: metricsError } = await supabase
    .from('kpi_metrics_view')
    .select('id, last_entry_date')
    .in(
      'id',
      kpisData.map((k) => k.id),
    )
    .not('last_entry_date', 'is', null)
    .order('last_entry_date', { ascending: false });

  if (metricsError) {
    throw metricsError;
  }

  const metricsMap = new Map(
    (metricsData || []).map((m) => [m.id, m.last_entry_date]),
  );

  const kpisWithMetrics = kpisData
    .map((kpi) => ({
      ...kpi,
      lastEntryDate: metricsMap.get(kpi.id) || null,
    }))
    .filter((kpi) => kpi.lastEntryDate !== null)
    .sort((a, b) => {
      return (
        new Date(b.lastEntryDate || '').getTime() -
        new Date(a.lastEntryDate || '').getTime()
      );
    })
    .slice(0, 4);

  const kpisWithContractors = await Promise.all(
    kpisWithMetrics.map(async (kpi) => {
      const project = kpi.project!;
      const projectId = project.id;
      const projectType = project.type;

      let contractorsQuery = supabase
        .from('project_contractors')
        .select('*, partner_alias:partner_aliases!inner(*)');

      if (projectType === 'project') {
        contractorsQuery = contractorsQuery.eq('project_id', projectId);
      } else if (projectType === 'subproject_client') {
        contractorsQuery = contractorsQuery.eq(
          'subproject_client_view_id',
          projectId,
        );
      } else if (projectType === 'subproject_contractor') {
        contractorsQuery = contractorsQuery.eq('subproject_view_id', projectId);
      }

      const { data: contractors, error: contractorsError } =
        await contractorsQuery;

      if (contractorsError) {
        throw contractorsError;
      }

      // Fetch the last tracker organization for this KPI
      // Step 1: Get latest entry's project_id
      const { data: lastEntryData, error: lastEntryError } = await supabase
        .from('kpi_tracking_entries')
        .select('id, created_at, project_id')
        .eq('kpi_id', kpi.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (lastEntryError) {
        throw lastEntryError;
      }

      let lastTrackerOrg: LastTrackerOrg | null = null;

      if (lastEntryData) {
        // Step 2: Try direct project → org lookup
        const { data: projectOrgData } = await supabase
          .from('projects')
          .select('org:orgs!projects_org_id_fkey(id, name, image_url)')
          .eq('id', lastEntryData.project_id)
          .maybeSingle();

        if (projectOrgData?.org) {
          const org = projectOrgData.org as unknown as LastTrackerOrg;
          lastTrackerOrg = {
            id: org.id,
            name: org.name,
            image_url: org.image_url,
          };
        }

        // Step 3: Fallback – use contractor info already fetched
        if (!lastTrackerOrg && contractors && contractors.length > 0) {
          const firstContractor = contractors[0] as any;
          const partnerOrgId = firstContractor?.partner_alias?.partner_orga_id;
          if (partnerOrgId) {
            const { data: orgData } = await supabase
              .from('orgs')
              .select('id, name, image_url')
              .eq('id', partnerOrgId)
              .maybeSingle();
            if (orgData) {
              lastTrackerOrg = orgData;
            }
          }
        }
      }

      return {
        ...kpi,
        project: project,
        contractors: contractors || [],
        lastEntryDate: kpi.lastEntryDate,
        lastTrackerOrg,
      };
    }),
  );

  return kpisWithContractors;
};
